<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h3 v-if="!$props.user">Phone</h3>
            <phone-input v-if="!$props.user" v-model="formData.Phone" />
            <template v-if="!isNew">
                <h3>New Password</h3>
                <input type="password" autocomplete="new-password" v-model="formData.Password"/>
                <div style="margin-bottom:15px;font-size:13px;opacity:0.7">Leave blank to keep the current password</div>
            </template>
            <h3>First Name</h3>
            <input type="text" v-model="formData.FirstName"/>
            <h3>Last Name</h3>
            <input type="text" v-model="formData.LastName"/>
            <h3>Location</h3>
            <select v-model="formData.Location">
                <option v-for="(location, i) in $store.state.user.locations" :key="i" :value="location">{{ location }}</option>
            </select>
            <h3>Additional Locations</h3>
            <div style="margin-bottom: 20px;max-height:120px;overflow:auto">
                <template v-for="(location, i) in formData.AdditionalLocationsNew" :key="i">
                    <div v-if="location.name != formData.Location" class="checkbox-input">
                        <input type="checkbox" v-model="location.isChecked" />
                        <div>{{ location.name }}</div>
                    </div>
                </template>
            </div>
            <h3>Departments</h3>
            <div style="margin-bottom: 20px;max-height:120px;overflow:auto">
                <div v-for="(department, i) in formData.DepartmentsNew" :key="i" class="checkbox-input">
                    <input type="checkbox" v-model="department.isChecked" />
                    <div>{{ department.name }}</div>
                </div>
            </div>
            <div class="checkbox-input">
                <input type="checkbox" v-model="formData.InActive" />
                <h3 style="margin:0">Inactive?</h3>
            </div>

            <input :class="disableButton ? 'disable-button' : ''" type="submit" value="Submit" @click="submit()" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import PhoneInput from '@/components/PhoneInput.vue';
import { normalizePhone, PHONE_ERROR } from '@/phone';

export default defineComponent({
    name: 'AddEditPublicUser',
    props: [ 'user' ],
    components: {
        PhoneInput
    },
    data() {
        return {
            disableButton: false,
            formData: {
                Phone: '',
                Password: '',
                FirstName: '',
                LastName: '',
                Location: '',
                AdditionalLocationsJSON: '',
                AdditionalLocationsNew: [] as any,
                InActive: false,
                DepartmentsJSON: '',
                DepartmentsNew: [] as any,
                SendActiveText: false
            },
            requiredFields: [
                'Phone', 'FirstName', 'LastName', 'Location'
            ],
            isNew: true,
            previousInactive: false
        }
    },
    async created() {
        if(this.$props.user) {
            this.isNew = false;
            this.formData = this.$props.user;
            // Never display or resubmit the existing password hash - blank means "leave unchanged"
            this.formData.Password = '';
            this.previousInactive = this.formData.InActive;
        }

        this.formData.DepartmentsNew = [];
        for(const department of this.$store.state.user.departments) {
            this.formData.DepartmentsNew.push({
                name: department,
                isChecked: false
            })
        }

        let additionalLocations: string[] = [];
        if(this.$props.user?.AdditionalLocationsJSON) {
            try {
                additionalLocations = JSON.parse(this.$props.user.AdditionalLocationsJSON);
            } catch(e) {
                // nothing
            }
        }
        this.formData.AdditionalLocationsNew = [];
        for(const location of this.$store.state.user.locations) {
            this.formData.AdditionalLocationsNew.push({
                name: location,
                isChecked: additionalLocations.includes(location)
            })
        }

        if(this.$props.user) {
            if(this.$props.user.DepartmentsJSON) {
                const departmentsJSON = JSON.parse(this.$props.user.DepartmentsJSON);
                for(const dep of departmentsJSON) {
                    const formDep = this.formData.DepartmentsNew.find((d: any) => {
                        return d.name == dep;
                    })
                    if(formDep) {
                        formDep.isChecked = true;
                    }
                }
            }
        }
    },
    methods: {
        async submit() {
            const phone = normalizePhone(this.formData.Phone);
            if(!phone) {
                alert(PHONE_ERROR);
                return;
            }
            this.formData.Phone = phone;

            const checkedDepartments = this.formData.DepartmentsNew.filter((dep: any) => {
                return dep.isChecked;
            })
            if(!checkedDepartments.length) {
                alert('Include at least one department');
                return;
            }
            const departmentsJSON = [];
            for(const dep of checkedDepartments) {
                departmentsJSON.push(dep.name);
            }
            this.formData.DepartmentsJSON = JSON.stringify(departmentsJSON);

            // the primary location is hidden from the list, so never save it as an additional one
            const additionalLocations = this.formData.AdditionalLocationsNew
                .filter((loc: any) => loc.isChecked && loc.name != this.formData.Location)
                .map((loc: any) => loc.name);
            this.formData.AdditionalLocationsJSON = JSON.stringify(additionalLocations);

            for(const field of this.requiredFields) {
                if(!(this.formData as any)[field]) {
                    alert(field + ' is required');
                    return;
                }
            }

            this.disableButton = true;

            this.formData.SendActiveText = false;
            if(this.isNew) {
                if(!this.formData.InActive) {
                    this.formData.SendActiveText = true;
                }
            } else {
                if(!this.formData.InActive && this.previousInactive) {
                    this.formData.SendActiveText = true;
                }
            }

            await axios.post(`${this.$store.state.apiUrl}/api/publicusers`, this.formData)
                .then(resp => {
                    // Saving created a new employee record (adding or editing), so it opens next to be filled in
                    if(resp.data?.employeeCreated) {
                        this.$router.push('/employee/' + resp.data.employeeID + '?edit=t');
                    }
                    this.$emit('close');
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error')
                })

            this.disableButton = false;
        }
    }
})
</script>